// 1. React and core dependencies

// 2. Internal components
import { Button } from '~/components/ui/button';
import { Spinner } from '~/components/ui/spinner';

/**
 * Props for AuthorizationActions component.
 */
export interface AuthorizationActionsProps {
  onAuthorize: () => void;
  onCancel: () => void;
  disabled: boolean;
  buttonText?: string;
  cancelText?: string;
  isAuthorizing?: boolean;
}

/**
 * Authorization action buttons for OAuth consent flow.
 *
 * Provides Authorize (primary) and Cancel (ghost) buttons.
 * Authorize button disabled until topic + at least one permission selected.
 * Cancel button redirects with error=access_denied.
 *
 * Per §TS.3.1: Function declaration for named export.
 * Per §TS.3.2: Props interface with descriptive naming.
 *
 * @example
 * ```tsx
 * <AuthorizationActions
 *   onAuthorize={handleAuthorize}
 *   onCancel={handleCancel}
 *   disabled={!selectedTopic || permissions.length === 0}
 * />
 * ```
 */
export function AuthorizationActions({
  onAuthorize,
  onCancel,
  disabled,
  buttonText = 'Authorize',
  cancelText = 'Deny',
  isAuthorizing = false,
}: AuthorizationActionsProps) {
  return (
    <div className="mt-6 flex flex-wrap justify-end gap-4">
      {/* Cancel Button - always enabled unless authorizing */}
      <Button
        variant="ghost"
        onClick={onCancel}
        disabled={isAuthorizing}
        data-testid="cancel-button"
      >
        {cancelText}
      </Button>

      {/* Authorize Button - primary action */}
      <Button
        variant="default"
        onClick={onAuthorize}
        disabled={disabled || isAuthorizing}
        aria-busy={isAuthorizing}
        data-testid="authorize-button"
        className="min-w-[140px]"
      >
        {isAuthorizing && <Spinner data-icon="inline-start" aria-hidden="true" />}
        {isAuthorizing ? 'Authorizing…' : buttonText}
      </Button>
    </div>
  );
}
